'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { Search } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import type { Conversation } from '@/lib/inbox/inboxClient'

function initialsFrom(text?: string) {
  const t = String(text || '').trim()

  if (!t) return 'DM'

  const parts = t
    .split(/\s+/)
    .slice(0, 2)

  return (
    parts
      .map((p) => p[0]?.toUpperCase())
      .join('') || 'DM'
  )
}

function formatCompactTime(ts?: string | null) {
  if (!ts) return ''

  const date = new Date(ts)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const now = new Date()

  const diffMs =
    now.getTime() - date.getTime()

  const diffMinutes =
    Math.floor(diffMs / 60_000)

  if (diffMinutes < 1) {
    return 'Ahora'
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const diffHours =
    Math.floor(diffMinutes / 60)

  if (diffHours < 24) {
    return `${diffHours}h`
  }

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)

  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()

  if (isYesterday) {
    return 'Ayer'
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  })
}

export function MessagesPopoverContent({
  conversations,
  userId,
  onOpenConversation,
}: {
  conversations: Conversation[]
  userId: number
  onOpenConversation: (
    conversationId: number
  ) => void
}) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()

    if (!q) {
      return conversations
    }

    return conversations.filter((c) => {
      const name =
        c.is_group
          ? c.title || ''
          : c.peer?.name || c.title || ''

      const preview =
        c.last_body || ''

      return (
        name.toLowerCase().includes(q) ||
        preview.toLowerCase().includes(q)
      )
    })
  }, [conversations, query])

  return (
    <div className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="text-sm font-semibold">
          Mensajes
        </div>

        <Link
          href="/inbox"
          className="text-xs font-medium text-primary hover:underline"
        >
          Ver todos
        </Link>
      </div>

      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

          <Input
            value={query}
            onChange={(e) =>
              setQuery(e.target.value)
            }
            placeholder="Buscar conversaciones"
            className="h-10 pl-9"
          />
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No hay conversaciones.
          </div>
        ) : (
          filtered.map((c) => {
            const displayName =
              c.is_group
                ? c.title ||
                  `Grupo #${c.conversation_id}`
                : c.peer?.name ||
                  c.title ||
                  `Conversación #${c.conversation_id}`

            const avatarUrl =
              c.is_group
                ? null
                : c.peer?.avatar_url || null

            const lastMessageId =
              Number(
                c.last_message_id || 0
              )

            const myRead =
              Number(
                c.my_last_read_message_id || 0
              )

            const lastSenderId =
            Number(c.last_sender_id || 0)

            const unread =
            lastMessageId > myRead &&
            lastSenderId !== Number(userId)

            return (
              <button
                key={c.conversation_id}
                type="button"
                onClick={() =>
                  onOpenConversation(
                    c.conversation_id
                  )
                }
                className={cn(
                  'flex w-full items-start gap-3 border-t px-4 py-3 text-left transition',
                  'hover:bg-muted/50',
                  unread &&
                    'bg-primary/[0.035]'
                )}
              >
                <Avatar className="h-10 w-10 shrink-0">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : null}

                  <AvatarFallback className="text-xs font-semibold">
                    {initialsFrom(
                      displayName
                    )}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'min-w-0 flex-1 truncate text-sm',
                        unread
                          ? 'font-semibold text-foreground'
                          : 'font-medium text-foreground'
                      )}
                    >
                      {displayName}
                    </div>

                    <div className="shrink-0 text-[11px] text-muted-foreground">
                      {formatCompactTime(
                        c.last_message_at
                      )}
                    </div>
                  </div>

                  <div className="mt-0.5 flex items-center gap-2">
                    <div
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs',
                        unread
                          ? 'text-foreground/80'
                          : 'text-muted-foreground'
                      )}
                    >
                      {c.last_body || '—'}
                    </div>

                    {unread ? (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                    ) : null}
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}